import Button from "app/components/button/button";
import { Currency } from "app/components/ido-details/sale-widget/sale-widget";
import CircleLoader from "app/components/loader/CircleLoader";
import { campaignIds } from "app/enums";
import useWidth from "app/hooks/useWidth";
import { useErrorLogger, useUserActionLogger } from "app/hooks/useLogger";
import { NetworkError } from "app/types/logging.types";
import { useTheme } from "app/shared";
import { chainIdToNetwork, IPrize, NetworkOption, Slice, slices, SpinResponse, stableTokenAddresses, } from "app/types";
import { getPrizeColor } from "app/utils/prizeRenderer";
import { ITEM_WIDTH, REPEAT_COUNT_RENDER, REPEAT_COUNT_SPIN, } from "app/types/constants";
import { formatTime } from "app/utils/formatTime";
import { generateTrack } from "app/utils/generateTrack";
import solynx10 from "assets/img/case/10$olynx.png";
import karma1200 from "assets/img/case/1200k.png";
import solynx15 from "assets/img/case/15$olynx.png";
import usdt15 from "assets/img/case/15usdt.png";
import karma1600 from "assets/img/case/1600k.png";
import karma40000 from "assets/img/case/40000k.png";
import karma400 from "assets/img/case/400k.png";
import karma8000 from "assets/img/case/8000k.png";
import karma800 from "assets/img/case/800k.png";
import alphapioneernft from "assets/img/case/alphapioneernft.png";
import extraspin from "assets/img/case/extraspin.png";
import fiveSpins from "assets/img/case/five_spins.jpg";
import hornnft from "assets/img/case/hornnft.png";
import jackpot from "assets/img/case/jackpot.png";
import legnft from "assets/img/case/legnft.png";
import mruni from "assets/img/case/mruninft.png";
import oneSpin from "assets/img/case/one_spin.jpg";
import piggyhornnft from "assets/img/case/piggyhornnft.png";
import threeSpins from "assets/img/case/three_spins.jpg";
import tokenairdrop from "assets/img/case/tokenairdrop.png";
import xardiannode from "assets/img/case/xardian_node.png";
import xforgephone from "assets/img/case/xforge_phone.png";
import axios from "axios";
import { ethers } from "ethers";
import { parseUnits } from "ethers/lib/utils";
import { FC, useEffect, useRef, useState } from "react";
import tokenService from "services/token.service";
import {
    useGetAllRoulettesQuery,
    useGetRouletteByIdQuery,
    useGetUserKarmaQuery,
    useProcessTxMutation,
    useSpinRouletteMutation,
} from "store/api/new.api";
import { useAccount, useSendTransaction } from "wagmi";
import NetworkSelect from "./NetworkSelector";
import FreeSpinIndicator from "./SpinIndicator";
import TokenSelect from "./TokenSelect";
import TwitterRepostModal from "./TweetButton";

import styles from "./CaseOpener.module.scss";

// Ticket packages configuration
const TICKET_OPTIONS = {
    small: {
        ticketCount: 5,
        price: 5,
        label: "🎯 Starter Pack",
        pricePerSpin: 5,
    },
    medium: {
        ticketCount: 15,
        price: 12,
        label: "🔥 Value Pack",
        pricePerSpin: 0.8,
    },
    big: {
        ticketCount: 30,
        price: 20,
        label: "💎 Best Deal",
        pricePerSpin: 0.66,
    },
} as const;

type TicketOptionKey = keyof typeof TICKET_OPTIONS;

// Default image for unknown prizes
const defaultPrizeImage = jackpot;

// IMPORTANT: Static slice images for visual display
// ALL static prizes are shown on UI (even if not winnable)
// New prizes from backend get added to display automatically
// BUT only backend prizes can actually be won (see createSlicesFromPrizes)
const sliceImages: Record<string, string> = {
    "Extra Spin": extraspin,
    "Token Airdrop": tokenairdrop,
    Jackpot: jackpot,
    "AlphaPioneer NFT": alphapioneernft,
    "15 USDT": usdt15,
    "+1200 Karma": karma1200,
    "Leg NFT": legnft,
    "+40000 Karma": karma40000,
    "$15 in $oLYNX": solynx15,
    "+800 Karma": karma800,
    "Mr.Uni NFT": piggyhornnft,
    "Horn NFT": hornnft,
    "Xardian Node": xardiannode,
    "+1600 Karma": karma1600,
    "+8000 Karma": karma8000,
    "XForge Phone": xforgephone,
    "Butt NFT": mruni,
    "+400 Karma": karma400,
    "$10 in $oLYNX": solynx10,
    "+500 Karma": karma400, // Reuse similar karma image
};

/**
 * Gets the image for a prize, returns default if not found
 */
const getPrizeImage = (prizeLabel: string, prize?: IPrize): string => {
    // First try exact match
    if (sliceImages[prizeLabel]) {
        return sliceImages[prizeLabel];
    }

    // If prize object is provided, use type-based mapping
    if (prize) {
        const prizeType = typeof prize.type === 'string' ? prize.type.toUpperCase() : prize.type;

        switch (prizeType) {
            case 'USDT':
                return usdt15; // USDT specific image
            case 'TOKEN':
                // Check for specific token symbols
                const symbol = prize.tokenConfig?.symbol?.toUpperCase();
                if (symbol === 'USDT') {
                    return usdt15;
                }
                return tokenairdrop; // Generic token image
            case 'FREE_SPIN':
                return extraspin;
            case 'KARMA':
                // Try to match karma by value
                if (prizeLabel.includes('Karma') || prizeLabel.includes('karma')) {
                    return karma400; // Generic karma image
                }
                return defaultPrizeImage;
            case 'NFT':
            case 'ALPHA_NFT':
                return jackpot; // Generic NFT/prize image
            default:
                return defaultPrizeImage;
        }
    }

    return defaultPrizeImage;
};

/**
 * Creates display slices by merging backend prizes with static slices
 *
 * Display Logic:
 * - Always show ALL static slices (even if not winnable)
 * - If backend prize exists in static slices -> just mark as winnable
 * - If backend prize is NEW (not in static) -> ADD it to display AND mark as winnable
 *
 * Win Logic:
 * - ONLY prizes from backend can actually be won (tracked in winnableLabels)
 */
const createSlicesFromPrizes = (prizes: IPrize[]): {
    displaySlices: Slice[],
    winnableLabels: Set<string>
} => {
    if (!prizes || prizes.length === 0) {
        // Return static slices for display but mark none as winnable
        return { displaySlices: slices, winnableLabels: new Set() };
    }

    // Track which prize names can actually be won (from backend)
    const winnableLabels = new Set(prizes.map(p => p.name));

    // Start with all static slices for visual display
    const displaySlices = [...slices];

    // Find prizes from backend that are NOT in static slices
    const staticLabels = new Set(slices.map(s => s.label));

    prizes.forEach((prize) => {
        if (!staticLabels.has(prize.name)) {
            // This is a NEW prize not in static list - add it to display
            displaySlices.push({
                label: prize.name,
                color: getPrizeColor(prize as any),
            });
        }
    });

    return { displaySlices, winnableLabels };
};

interface ICaseOpener {
    _id: string | null;
}

const CaseOpener: FC<ICaseOpener> = ({ _id }) => {
    const { theme } = useTheme();
    const { address, chainId } = useAccount();
    const account = address || "";

    // Logging hooks
    const { logButtonClick } = useUserActionLogger();
    const { logJavaScriptError, logNetworkError } = useErrorLogger();

    const [spinResults, setSpinResults] = useState<SpinResponse | null>(null);
    const [isAwardLoading, setIsAwardLoading] = useState(false);
    const [claimError, setClaimError] = useState<string | null>(null);
    const [claimSuccess, setClaimSuccess] = useState(false);
    const [spinError, setSpinError] = useState<string | null>(null);
    const [spinMultiplier, setSpinMultiplier] = useState<1 | 5 | 10>(1);
    const [batchSpinsLeft, setBatchSpinsLeft] = useState<number>(0);
    const [displayResults, setDisplayResults] = useState<
        Array<{ name: string; value: string }>
    >([]);
    const [fastSpinEnabled, setFastSpinEnabled] = useState(false);
    const { isSmallMobile, isMobile, isTablet, isBigTablet, isSmallDesktop } =
        useWidth();
    const VIEWPORT_WIDTH =
        isSmallMobile || isMobile
            ? 335
            : isTablet || isBigTablet || isSmallDesktop
                ? 400
                : 600;

    const { data: roulettes, refetch: rouletteRefetch } =
        useGetAllRoulettesQuery();
    const [processTxMutation] = useProcessTxMutation();
    const [spinRoulette, { isLoading: isSpinLoading }] =
        useSpinRouletteMutation();
    const { data: karma, refetch: karmaRefetch } = useGetUserKarmaQuery({
        account,
        campaignIds,
    });
    // const { data: activity, refetch: activityRefetch } = useGetClaimrActivityQuery({ platform: 'web3', account, campaignIds });

    // const { sendTransaction } = useSendTransaction();

    const [currentTicketOptionKey, setCurrentTicketOptionKey] = useState<
        string | null
    >(null);

    const { data: roulette, refetch: rouletteByIdRefetch } =
        useGetRouletteByIdQuery({ rouletteId: _id || "", address }, { skip: !_id });

    const selectedRoulette = roulette;

    const availableTicketCount =
        selectedRoulette?.tickets?.filter((ticket: any) => !ticket.used).length ||
        0;
    const spinCount =
        selectedRoulette?.tickets?.filter((ticket: any) => ticket.used).length || 0;

    const [isTwitterModalOpen, setIsTwitterModalOpen] = useState(false);
    const [totalWonValue, setTotalWonValue] = useState(0);
    const [lastWonValue, setLastWonValue] = useState<string | null>(null);
    const [hash, setHash] = useState<string | null>(null);
    const [cost, setCost] = useState<string | null>(null);
    const [isCaseOpened, setIsCaseOpened] = useState(false);
    const [purchaseError, setPurchaseError] = useState<string | null>(null);
    const [scrollOffset, setScrollOffset] = useState(0);
    const [isSpinning, setIsSpinning] = useState(false);
    const [transitionDuration, setTransitionDuration] = useState(4000);
    const [result, setResult] = useState<string | null>(null);
    const [timeLeft, setTimeLeft] = useState(8 * 60 * 60);
    const [loading, setIsLoading] = useState(false);
    const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);
    const [selectedToken, setSelectedToken] = useState<
        Currency.USDC | Currency.USDT
    >(Currency.USDT);
    const prevTimeLeftRef = useRef<number | null>(null);

    const recipient: `0x${string}` = selectedRoulette?.recepient as `0x${string}`;

    const [selectedNetwork, setSelectedNetwork] = useState<NetworkOption>(
        chainId ? chainIdToNetwork[chainId] || "ETH" : "ETH"
    );

    const tokenAddresses = stableTokenAddresses[selectedNetwork];

    // Create display slices (static for visuals) and track winnable prizes (from backend)
    const { displaySlices: dynamicSlices, winnableLabels } = selectedRoulette?.prizes
        ? createSlicesFromPrizes(selectedRoulette.prizes)
        : { displaySlices: slices, winnableLabels: new Set() };

    // Create a mapping from prize label to prize object for image lookup
    const prizeLabelMap = selectedRoulette?.prizes
        ? selectedRoulette.prizes.reduce((acc: Record<string, IPrize>, prize: IPrize) => {
            acc[prize.name] = prize;
            return acc;
        }, {} as Record<string, IPrize>)
        : {};

    const renderTrack = generateTrack(REPEAT_COUNT_RENDER, dynamicSlices);

    const trackWidth = renderTrack.length * ITEM_WIDTH;
    const maxOffset = trackWidth - VIEWPORT_WIDTH;

    // useEffect(() => {
    // 	if (activity?.rewards) {
    // 		const sumXP = activity.rewards.reduce((acc: number, item: any) => {
    // 			const xpValue = item.xp ?? 0;
    // 			return acc + xpValue;
    // 		}, 0);
    // 		setTotalWonValue(sumXP);
    // 	}
    // }, [activity]);

    useEffect(() => {
        if (chainId) {
            setSelectedNetwork(chainIdToNetwork[chainId] || "ETH");
        }
    }, [chainId]);

    useEffect(() => {
        const updateTimeLeft = () => {
            if (selectedRoulette?.tickets && selectedRoulette.tickets.length > 0) {
                const usedTickets = selectedRoulette.tickets.filter(
                    (ticket: any) => ticket.used && ticket.usedAt
                );
                if (usedTickets.length > 0) {
                    const latestUsedAt = Math.max(
                        ...usedTickets.map((ticket: any) =>
                            new Date(ticket.usedAt).getTime()
                        )
                    );
                    const eightHoursInMs = 8 * 60 * 60 * 1000;
                    const elapsed = Date.now() - latestUsedAt;
                    const newTimeLeft = Math.max(eightHoursInMs - elapsed, 0);
                    setTimeLeft(Math.floor(newTimeLeft / 1000));
                } else {
                    setTimeLeft(0);
                }
            } else {
                setTimeLeft(0);
            }
        };

        updateTimeLeft();
        const intervalId = setInterval(updateTimeLeft, 1000);
        return () => clearInterval(intervalId);
    }, [selectedRoulette?.tickets]);

    useEffect(() => {
        const prev = prevTimeLeftRef.current;
        const curr = timeLeft;

        if (
            prev !== null &&
            prev > 0 &&
            curr === 0 &&
            availableTicketCount === 0 &&
            _id
        ) {
            rouletteByIdRefetch();
        }

        prevTimeLeftRef.current = curr;
    }, [timeLeft, availableTicketCount, rouletteByIdRefetch, _id]);

    const runAnimation = async (skipAnimation = false, bundleSize?: 5 | 10) => {
        try {
            if (!roulettes || roulettes.length === 0) {
                console.error("No available roulettes");
                return;
            }
            const rouletteId = selectedRoulette?._id;
            if (!rouletteId) {
                console.error("No rouletteId available");
                return;
            }

            const response = await spinRoulette({
                rouletteId,
                ...(bundleSize && { bundleSize }),
            }).unwrap();

            // Log successful spin response
            logButtonClick('roulette_spin_success', 'CaseOpener', {
                rouletteId,
                bundleSize: bundleSize || 1,
                address,
                prizes: Array.isArray(response) ? response.map(r => r.prize.name) : [response.prize.name],
                timestamp: new Date().toISOString()
            });

            const spinResults = Array.isArray(response) ? response : [response];
            setSpinResults(spinResults);

            if (bundleSize) {
                setDisplayResults(
                    spinResults.map((res) => ({
                        name: res.prize.name,
                        value: res.prize.value,
                    }))
                );
                return;
            }

            const singleResult = spinResults[0];
            const { prize } = singleResult;
            let targetPrizeLabel = prize.name;
            const targetPrizeValueStr = prize.value;

            // CRITICAL: Validate that the prize from backend is actually winnable
            if (!winnableLabels.has(targetPrizeLabel)) {
                // Try to extract original prize name from "(instead of ...)" pattern
                const insteadOfMatch = targetPrizeLabel.match(/\(instead of (.+)\)$/);
                const originalPrizeName = insteadOfMatch ? insteadOfMatch[1].trim() : null;

                // Check if original prize name exists in winnable labels
                if (originalPrizeName && winnableLabels.has(originalPrizeName)) {
                    console.log("Prize was substituted by backend, using original prize name for display", {
                        substitutedPrize: targetPrizeLabel,
                        originalPrize: originalPrizeName
                    });
                    targetPrizeLabel = originalPrizeName;
                } else {
                    console.error("Backend returned invalid prize that doesn't exist in roulette prizes", {
                        returnedPrize: targetPrizeLabel,
                        extractedOriginal: originalPrizeName,
                        winnablePrizes: Array.from(winnableLabels),
                        allPrizes: selectedRoulette?.prizes?.map((p: IPrize) => p.name)
                    });

                    setSpinError("Invalid prize returned. This shouldn't happen. Please contact support.");
                    setIsSpinning(false);
                    return;
                }
            }

            if (skipAnimation || fastSpinEnabled) {
                setResult(targetPrizeLabel);
                const prizeValueNumber = parseInt(
                    targetPrizeValueStr.replace(/[^\d]/g, ""),
                    10
                );
                setTotalWonValue(
                    (prev) => prev + (isNaN(prizeValueNumber) ? 0 : prizeValueNumber)
                );
                setLastWonValue(targetPrizeLabel);
                return;
            }

            // Ensure the won prize is in dynamicSlices (it might not be if backend added new prize)
            let prizeIndex = dynamicSlices.findIndex((p) => p.label === targetPrizeLabel);

            // If prize not found, add it dynamically to a temporary slices array
            let slicesToUse = dynamicSlices;
            if (prizeIndex === -1 && prize) {
                slicesToUse = [...dynamicSlices, {
                    label: targetPrizeLabel,
                    color: getPrizeColor(prize as any),
                }];
                prizeIndex = slicesToUse.length - 1;
            }
            if (prizeIndex === -1) {
                console.error("Prize not found in slices", {
                    targetPrizeLabel,
                    availableLabels: dynamicSlices.map((s: Slice) => s.label),
                    prizesFromBackend: selectedRoulette?.prizes?.map((p: IPrize) => p.name)
                });

                // Fallback: skip animation and just show the result
                setResult(targetPrizeLabel);
                const prizeValueNumber = parseInt(
                    targetPrizeValueStr.replace(/[^\d]/g, ""),
                    10
                );
                setTotalWonValue(
                    (prev) => prev + (isNaN(prizeValueNumber) ? 0 : prizeValueNumber)
                );
                setLastWonValue(targetPrizeLabel);
                return;
            }

            const spinDuration = 5000 + Math.random() * 2000;
            const targetIndex = (REPEAT_COUNT_SPIN - 1) * slicesToUse.length + prizeIndex;
            const idealOffset =
                targetIndex * ITEM_WIDTH + ITEM_WIDTH / 2 - VIEWPORT_WIDTH / 2;
            const finalIdealOffset = idealOffset;
            const bounce = Math.random() < 0.9;
            const bounceOffset = bounce ? Math.round(Math.random() * 60 - 30) : 0;
            const computedOffset = finalIdealOffset + bounceOffset;
            const targetOffset = Math.min(computedOffset, maxOffset);

            setIsSpinning(true);
            setTransitionDuration(spinDuration);
            setScrollOffset(targetOffset);

            setTimeout(() => {
                if (bounce) {
                    setTransitionDuration(500);
                    setScrollOffset(Math.min(finalIdealOffset, maxOffset));
                    setTimeout(() => {
                        setIsSpinning(false);
                        setResult(targetPrizeLabel);
                        const prizeValueNumber = parseInt(
                            targetPrizeValueStr.replace(/[^\d]/g, ""),
                            10
                        );
                        setTotalWonValue(
                            (prev) => prev + (isNaN(prizeValueNumber) ? 0 : prizeValueNumber)
                        );
                        setLastWonValue(targetPrizeLabel);
                    }, 500);
                } else {
                    setIsSpinning(false);
                    setResult(targetPrizeLabel);
                    const prizeValueNumber = parseInt(
                        targetPrizeValueStr.replace(/[^\d]/g, ""),
                        10
                    );
                    setTotalWonValue(
                        (prev) => prev + (isNaN(prizeValueNumber) ? 0 : prizeValueNumber)
                    );
                    setLastWonValue(targetPrizeLabel);
                }
            }, spinDuration);
        } catch (error: any) {
            console.error("Error during spin:", error);

            // Extract user-friendly error message
            let errorMessage = 'Something went wrong. Please try again in a moment.';

            if (error?.data?.message) {
                const backendMessage = error.data.message;

                // Improve specific error messages
                if (backendMessage.includes('non-numeric argument') || backendMessage.includes('totalReserved')) {
                    errorMessage = 'We\'re experiencing technical difficulties with the prize pool. Our team has been notified. Please try again shortly.';
                } else if (backendMessage.includes('No available prizes')) {
                    errorMessage = 'All prizes have been claimed for now. Check back soon for more!';
                } else if (backendMessage.includes('Already used')) {
                    errorMessage = 'This spin has already been used. Please refresh the page to see your updated spins.';
                } else if (backendMessage.includes('Not enough purchased tickets for the requested spin bundle')) {
                    errorMessage = `${bundleSize}x bundle requires purchased spins. You need to buy spins to use this feature.`;
                } else if (backendMessage.includes('Not enough tickets')) {
                    errorMessage = 'You don\'t have enough spins. Purchase more to continue playing!';
                } else {
                    // Show backend message directly
                    errorMessage = backendMessage;
                }
            } else if (error?.message) {
                errorMessage = error.message;
            }

            setSpinError(errorMessage);
            setIsSpinning(false);

            const networkError = new NetworkError(
                error?.data?.message || error?.message || 'Spin failed',
                'spinRoulette',
                error?.status
            );
            logNetworkError(networkError, 'spinRoulette', {
                component: 'CaseOpener',
                action: 'runAnimation',
                rouletteId: selectedRoulette?._id,
                bundleSize,
                address,
                errorMessage
            });
        }
    };

    const runBatchSpins = async (count: 5 | 10) => {
        if (availableTicketCount < count) {
            return;
        }

        setBatchSpinsLeft(count);
        setDisplayResults([]);

        await runAnimation(true, count);

        setBatchSpinsLeft(0);
        refetchAllData();
    };

    const refetchAllData = () => {
        setScrollOffset(0);
        setIsCaseOpened(false);
        setResult(null);
        rouletteRefetch();
        rouletteByIdRefetch();
        karmaRefetch();
    };

    const handleSpin = async () => {
        if (isSpinning || isSpinLoading) return;

        if (availableTicketCount === 0 && timeLeft > 0) return;

        // Clear previous spin error
        setSpinError(null);

        // Log spin button click
        logButtonClick('roulette_spin', 'CaseOpener', {
            rouletteId: _id,
            spinMultiplier,
            availableTicketCount,
            address,
            timestamp: new Date().toISOString()
        });

        try {
            if (spinMultiplier === 1) {
                if (!isCaseOpened) {
                    setIsCaseOpened(true);
                    setTimeout(() => {
                        runAnimation();
                    }, 50);
                } else {
                    runAnimation();
                }
            } else {
                await runBatchSpins(spinMultiplier as 5 | 10);
            }
        } catch (error) {
            logJavaScriptError(error as Error, 'CaseOpener', 'handleSpin', {
                rouletteId: _id,
                spinMultiplier,
                address
            });
        }
    };

    const handleMoreSpins = () => {
        setIsPurchaseModalOpen(true);
    };

    const { sendTransaction } = useSendTransaction({
        mutation: {
            onSuccess: async (data: any) => {
                console.log("Transaction sent:", data);
                setPurchaseError(null);

                await new Promise((resolve) => setTimeout(resolve, 5000));

                if (!chainId) {
                    const errorMessage = 'Chain ID is not available. Please check your wallet connection.';
                    setPurchaseError(errorMessage);
                    setIsLoading(false);
                    return;
                }

                const processTxPayload = {
                    txHash: data,
                    chainId,
                    rouletteId: selectedRoulette?._id,
                    ticketOptionKey: currentTicketOptionKey,
                    from: address, // Add sender address
                };

                try {
                    const processResponse = await processTxMutation(
                        processTxPayload
                    ).unwrap();
                    console.log("Backend response:", processResponse);

                    // Log successful purchase
                    logButtonClick('roulette_purchase_success', 'CaseOpener', {
                        rouletteId: selectedRoulette?._id,
                        ticketOptionKey: currentTicketOptionKey,
                        txHash: data,
                        address,
                        timestamp: new Date().toISOString()
                    });

                    setHash(data);
                    setCost(currentTicketOptionKey);
                } catch (backendError: any) {
                    console.error("Error processing backend transaction:", backendError);

                    let errorMessage = backendError?.data?.message ||
                        backendError?.message ||
                        'Failed to process purchase. Please contact support.';

                    // Improve Etherscan API error message
                    if (errorMessage.includes('Missing chainid parameter') ||
                        errorMessage.includes('chainid parameter')) {
                        errorMessage = 'Transaction verification failed. Please try again or contact support if the issue persists. Your wallet was not charged.';
                    }

                    setPurchaseError(errorMessage);

                    // Log purchase error
                    const networkError = new NetworkError(
                        errorMessage,
                        'roulette/processTx',
                        backendError?.status
                    );
                    logNetworkError(networkError, 'roulette/processTx', {
                        component: 'CaseOpener',
                        action: 'processTxMutation',
                        rouletteId: selectedRoulette?._id,
                        ticketOptionKey: currentTicketOptionKey,
                        txHash: data,
                        chainId, // Include chainId for debugging
                        errorMessage,
                        originalError: backendError?.data?.message || backendError?.message,
                        address,
                        timestamp: new Date().toISOString()
                    });
                } finally {
                    rouletteRefetch();
                    rouletteByIdRefetch();
                    setIsLoading(false);
                }
            },
            onError: (error: any) => {
                let errorMessage = error?.shortMessage || error?.message || 'Transaction failed. Please try again.';

                // Handle user rejection
                if (errorMessage.includes('User denied') ||
                    errorMessage.includes('User rejected') ||
                    errorMessage.includes('user rejected')) {
                    errorMessage = 'Transaction was cancelled. You can try again when ready.';
                }
                // Handle insufficient funds
                else if (errorMessage.includes('insufficient funds')) {
                    errorMessage = 'Insufficient funds in your wallet. Please add funds and try again.';
                }
                // Truncate very long error messages
                else if (errorMessage.length > 200) {
                    errorMessage = errorMessage.substring(0, 200) + '...';
                }

                setPurchaseError(errorMessage);

                // Log transaction error
                logJavaScriptError(error, 'CaseOpener', 'sendTransaction', {
                    rouletteId: selectedRoulette?._id,
                    ticketOptionKey: currentTicketOptionKey,
                    errorMessage,
                    originalError: error?.message,
                    address,
                    timestamp: new Date().toISOString()
                });

                setIsLoading(false);
            },
        },
    });

    const handlePurchase = async (ticketOptionKey: TicketOptionKey) => {
        const ticketOption = TICKET_OPTIONS[ticketOptionKey];
        const cost = ticketOption.price.toString();

        setCurrentTicketOptionKey(ticketOptionKey);
        setHash(null);
        setCost(null);
        setPurchaseError(null);
        setIsLoading(true);

        // Log purchase attempt
        logButtonClick('roulette_purchase_attempt', 'CaseOpener', {
            rouletteId: selectedRoulette?._id,
            ticketOptionKey,
            ticketCount: ticketOption.ticketCount,
            price: ticketOption.price,
            address,
            timestamp: new Date().toISOString()
        });

        try {
            const tokenAddress = tokenAddresses[selectedToken];
            const iface = new ethers.utils.Interface([
                "function transfer(address to, uint256 amount) public returns (bool)",
            ]);
            const decimals = chainId === 56 ? 18 : 6;
            const data = iface.encodeFunctionData("transfer", [
                recipient,
                parseUnits(cost, decimals),
            ]) as `0x${string}`;

            sendTransaction({
                to: tokenAddress,
                data: data,
            });
        } catch (error) {
            console.error("Error during purchase transaction:", error);

            let errorMessage = (error as Error)?.message || 'Failed to create transaction';

            // Handle common errors
            if (errorMessage.includes('User denied') ||
                errorMessage.includes('User rejected') ||
                errorMessage.includes('user rejected')) {
                errorMessage = 'Transaction was cancelled. You can try again when ready.';
            } else if (errorMessage.length > 200) {
                errorMessage = errorMessage.substring(0, 200) + '...';
            }

            setPurchaseError(errorMessage);

            logJavaScriptError(error as Error, 'CaseOpener', 'handlePurchase', {
                rouletteId: selectedRoulette?._id,
                ticketOptionKey,
                ticketCount: ticketOption.ticketCount,
                price: ticketOption.price,
                errorMessage,
                originalError: (error as Error)?.message,
                address,
                timestamp: new Date().toISOString()
            });

            setIsLoading(false);
        }
    };

    const onClose = () => {
        setHash(null);
        setCost(null);
        setPurchaseError(null);
        setIsPurchaseModalOpen(false);
        refetchAllData();
    };

    const handleCloseModal = () => {
        setResult(null);
        setDisplayResults([]);
        setSpinResults(null);
        setClaimError(null);
        setClaimSuccess(false);
    };

    const handleCloseSuccessModal = () => {
        setClaimSuccess(false);
        setResult(null);
        setDisplayResults([]);
        setSpinResults(null);
        setClaimError(null);
        refetchAllData();
    };

    const handleClaim = async () => {
        setIsAwardLoading(true);
        setClaimError(null);

        // Log claim button click
        const resultsArray = spinResults ? (Array.isArray(spinResults) ? spinResults : [spinResults]) : [];
        const ticketIds = resultsArray.map((res) => res.ticketId);

        logButtonClick('roulette_claim', 'CaseOpener', {
            rouletteId: _id,
            ticketIds,
            prizeCount: resultsArray.length,
            address,
            timestamp: new Date().toISOString()
        });

        try {
            if (!spinResults) {
                throw new Error("No spin results available");
            }

            const token = tokenService.tokens?.accessToken;
            if (!token) {
                throw new Error("Authentication required. Please reconnect your wallet.");
            }

            const queryString = ticketIds.map((id) => `ticketIds=${id}`).join("&");

            console.log('Making claim request with ticketIds:', ticketIds);

            const response = await axios.post(
                `${process.env.REACT_APP_API_SECONDARY}/roulette/award?${queryString}`,
                null,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            console.log('Claim response:', response.data);

            // Check if the API returned success: false
            if (response.data && response.data.success === false) {
                let errorMessage =
                    response.data.err?.message ||
                    response.data.err?.response ||
                    response.data.message ||
                    'Failed to claim prize. Please contact support.';

                // Improve error message for common cases
                if (errorMessage.includes('No valid reservation found')) {
                    errorMessage = 'This prize is not available anymore. The prize pool may have been updated. Please refresh and try spinning again.';
                }

                throw new Error(errorMessage);
            }

            // Log successful claim
            logButtonClick('roulette_claim_success', 'CaseOpener', {
                rouletteId: _id,
                ticketIds,
                prizeCount: resultsArray.length,
                address,
                timestamp: new Date().toISOString()
            });

            // Show success modal instead of immediately closing
            setClaimSuccess(true);
        } catch (err: any) {
            console.error("Error claiming prize:", err);
            console.error("Error response:", err.response);

            // Extract error message
            const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.error ||
                err.message ||
                "Failed to claim prize. Please try again.";

            // Log claim error
            const networkError = new NetworkError(
                errorMessage,
                'roulette/award',
                err.response?.status
            );
            logNetworkError(networkError, 'roulette/award', {
                component: 'CaseOpener',
                action: 'handleClaim',
                rouletteId: _id,
                ticketIds,
                errorMessage,
                address,
                timestamp: new Date().toISOString()
            });

            setClaimError(errorMessage);
            // Don't close modal on error - user needs to see the error
        } finally {
            setIsAwardLoading(false);
        }
    };

    const handleTwitterRepost = () => {
        setIsTwitterModalOpen(true);
    };

    const closeTwitterModal = () => {
        setIsTwitterModalOpen(false);
    };

    return (
        <div
            className={`${styles.container} ${
                theme === "dark" ? styles.darkTheme : ""
            }`}
        >
            <div className={styles.leftSide}>
                <div>
                    {!isCaseOpened ? (
                        <div className={styles.marquee}>
                            <div className={styles.pointer}></div>
                            <div className={styles.marqueeInner}>
                                {dynamicSlices.map((slice, index) => (
                                    <img
                                        key={index}
                                        src={getPrizeImage(slice.label, prizeLabelMap[slice.label])}
                                        alt={slice.label}
                                        className={styles.caseItemImage}
                                    />
                                ))}
                                {dynamicSlices.map((slice, index) => (
                                    <img
                                        key={"dup-" + index}
                                        src={getPrizeImage(slice.label, prizeLabelMap[slice.label])}
                                        alt={slice.label}
                                        className={styles.caseItemImage}
                                    />
                                ))}
                            </div>
                            <div className={styles.pointer}></div>
                        </div>
                    ) : (
                        <div className={styles.caseWrapper}>
                            <div className={styles.pointer}></div>
                            <div
                                className={styles.caseTrack}
                                style={{
                                    transform: `translateX(-${scrollOffset}px)`,
                                    transition: `transform ${transitionDuration}ms cubic-bezier(0.33, 1, 0.68, 1)`,
                                }}
                            >
                                {renderTrack.map((item, index) => (
                                    <div key={index} className={styles.caseItem}>
                                        <img
                                            src={getPrizeImage(item.label, prizeLabelMap[item.label])}
                                            alt={item.label}
                                            className={styles.caseItemImage}
                                        />
                                    </div>
                                ))}
                            </div>
                            <div className={styles.pointer}></div>
                        </div>
                    )}
                    <div className={styles.timer}>
                        {availableTicketCount > 0 ? (
                            <span>Spins Available: {availableTicketCount}</span>
                        ) : (
                            <span>⏳ Free spin available in: {formatTime(timeLeft)}</span>
                        )}
                    </div>
                    <div className={styles.spinTabs}>
                        <button
                            className={`${styles.spinTab} ${
                                spinMultiplier === 1 ? styles.active : ""
                            }`}
                            onClick={() => setSpinMultiplier(1)}
                        >
                            1x
                        </button>
                        <button
                            className={`${styles.spinTab} ${
                                spinMultiplier === 5 ? styles.active : ""
                            }`}
                            onClick={() => setSpinMultiplier(5)}
                            disabled={availableTicketCount < 5}
                        >
                            5x
                        </button>
                        <button
                            className={`${styles.spinTab} ${
                                spinMultiplier === 10 ? styles.active : ""
                            }`}
                            onClick={() => setSpinMultiplier(10)}
                            disabled={availableTicketCount < 10}
                        >
                            10x
                        </button>
                        <div
                            className={`${styles.toggleContainer} ${
                                theme === "dark" ? styles.darkTheme : ""
                            }`}
                        >
                            <span className={styles.toggleLabel}>Fast Spin</span>
                            <button
                                className={`${styles.toggle} ${
                                    fastSpinEnabled ? styles.active : ""
                                }`}
                                onClick={() => setFastSpinEnabled(!fastSpinEnabled)}
                                aria-label="Toggle fast spin"
                            >
                                <span className={styles.toggleHandle}/>
                            </button>
                        </div>
                    </div>
                </div>

                <div
                    className={`${styles.btnWrapper} ${
                        theme === "dark" ? styles.darkTheme : ""
                    }`}
                >
                    <Button
                        variant="pink"
                        onClick={handleSpin}
                        disabled={
                            isSpinning ||
                            (!availableTicketCount && timeLeft > 0) ||
                            batchSpinsLeft > 0
                        }
                    >
                        {batchSpinsLeft > 0
                            ? `Spinning (${batchSpinsLeft} left)`
                            : isSpinning || isSpinLoading
                                ? "Spinning..."
                                : `Spin ${spinMultiplier > 1 ? spinMultiplier : ""}${
                                    spinMultiplier > 1 ? "x" : ""
                                } Now!`}
                    </Button>
                    <Button variant="blue" onClick={handleMoreSpins}>
                        🔓 Unlock More Spins
                    </Button>
                </div>
            </div>

            <div className={styles.rightSide}>
                <div>
                    <h1 className={styles.title}>Spin Daily. Win Big. Repeat.</h1>
                    {/* <h1 className={styles.title1}>Your Daily Shot at Epic Rewards</h1> */}
                    <p
                        className={`${styles.subtitle1} ${
                            theme === "dark" ? styles.darkTheme : ""
                        }`}
                    >
                        Win Karma, NFTs, airdrops, special items & mystery rewards.
                    </p>
                    <p
                        className={`${styles.subtitle} ${
                            theme === "dark" ? styles.darkTheme : ""
                        }`}
                    >
                        Spin free now – or go big with bonus spins.
                    </p>
                    <div className={styles.infoGrid}>
                        <div className={styles.infoContainer}>
                            <div className={styles.header}>Free Spins Left Today</div>
                            {roulette && <FreeSpinIndicator roulette={roulette.tickets}/>}
                        </div>
                        {/* <div className={styles.value}>{spinCount}</div> */}
                        {/* <div className={styles.infoContainer}>
							<div className={styles.header}>Total Karma</div>
							<div className={styles.value}>{karma?.karmaView.toFixed()}</div>
						</div> */}
                        {/* <div className={styles.infoContainer}>
							<div className={styles.header}>Karma Gained</div>
							<div className={styles.value}>{totalWonValue}</div>
						</div> */}
                    </div>
                    <div className={styles.descriptions}>
                        <span className={styles.description}>• 1 Free Spin every 8h</span>
                        <span className={styles.description}>
              • Streak bonus: spin daily to unlock hidden rewards
            </span>
                        <span className={styles.description}>
              • Jackpot drops & rare items updated weekly
            </span>
                    </div>
                </div>
                <Button variant="blue" onClick={handleTwitterRepost}>
                    Get Free Spin
                </Button>
            </div>

            {result && lastWonValue && !claimSuccess && (
                <div className={styles.modal}>
                    <div
                        className={`${styles.modalContent} ${
                            theme === "dark" ? styles.darkTheme : ""
                        }`}
                    >
                        <button
                            className={styles.modalClose}
                            onClick={handleCloseModal}
                        >
                            ×
                        </button>
                        <h1 className={styles.modalTitle}>Congratulations! 🎉</h1>
                        <img
                            src={getPrizeImage(result, prizeLabelMap[result])}
                            alt={result}
                            className={styles.modalPrizeImage}
                        />
                        <p className={styles.modalText}>🎁 You have won {lastWonValue}!</p>
                        {claimError && (
                            <div className={styles.errorMessage}>
                                <span>❌</span>
                                <span>{claimError}</span>
                            </div>
                        )}
                        {isAwardLoading ? (
                            <Button variant="pink" disabled>
                                Claiming...
                            </Button>
                        ) : (
                            <Button variant="pink" onClick={handleClaim}>
                                {claimError ? 'Try Again' : 'Claim!'}
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {displayResults.length > 0 && !claimSuccess && (
                <div className={styles.modal}>
                    <div
                        className={`${styles.modalContent} ${
                            theme === "dark" ? styles.darkTheme : ""
                        }`}
                    >
                        <button
                            className={styles.modalClose}
                            onClick={handleCloseModal}
                        >
                            ×
                        </button>
                        <h1 className={styles.modalTitle}>Congratulations! 🎉</h1>
                        <div className={styles.batchResults}>
                            {displayResults.map((result, index) => (
                                <div key={index} className={styles.batchResultItem}>
                                    <img
                                        src={getPrizeImage(result.name, prizeLabelMap[result.name])}
                                        alt={result.name}
                                        className={styles.batchResultImage}
                                    />
                                    <p className={styles.modalText}>{result.name}</p>
                                </div>
                            ))}
                        </div>
                        {claimError && (
                            <div className={styles.errorMessage}>
                                <span>❌</span>
                                <span>{claimError}</span>
                            </div>
                        )}
                        <Button
                            variant="pink"
                            onClick={handleClaim}
                            disabled={isAwardLoading}
                        >
                            {isAwardLoading ? "Claiming..." : claimError ? "Try Again" : "Claim All Prizes!"}
                        </Button>
                    </div>
                </div>
            )}

            {claimSuccess && (
                <div className={styles.modal}>
                    <div
                        className={`${styles.modalContent} ${
                            theme === "dark" ? styles.darkTheme : ""
                        }`}
                    >
                        <button
                            className={styles.modalClose}
                            onClick={handleCloseSuccessModal}
                        >
                            ×
                        </button>
                        <h1 className={styles.modalTitle}>Success! ✅</h1>
                        <div className={styles.successMessage}>
                            <span>🎉</span>
                            <span>Your prizes have been claimed successfully!</span>
                        </div>
                        <p className={styles.modalText}>
                            Check your wallet and account to see your rewards.
                        </p>
                        <Button variant="pink" onClick={handleCloseSuccessModal}>
                            Awesome!
                        </Button>
                    </div>
                </div>
            )}

            {isPurchaseModalOpen && (
                <div className={styles.modal}>
                    <div
                        className={`${styles.modalContent} ${
                            theme === "dark" ? styles.darkTheme : ""
                        }`}
                    >
                        <button className={styles.modalClose} onClick={onClose}>
                            ×
                        </button>
                        {hash ? (
                            <div>
                                <h3 className={styles.messageTitle}>
                                    🎉 You’ve Got Extra Spins!
                                </h3>
                                <div className={styles.packageCard}>
                                    <img
                                        src={
                                            cost === "small"
                                                ? oneSpin
                                                : cost === "medium"
                                                    ? threeSpins
                                                    : fiveSpins
                                        }
                                        alt="spin pack"
                                        className={styles.packageImage}
                                    />
                                    <p className={styles.packageText}>
                                        {cost && TICKET_OPTIONS[cost as TicketOptionKey]
                                            ? `${TICKET_OPTIONS[cost as TicketOptionKey].label} — ${TICKET_OPTIONS[cost as TicketOptionKey].ticketCount} Spins`
                                            : "Spin Pack"}
                                    </p>
                                </div>
                                <p className={styles.messageText}>
                                    You’ve successfully unlocked your spin pack. Let’s see what
                                    you win!
                                </p>
                                <p className={styles.messageText}>
                                    Start spinning now — rewards are waiting.
                                </p>
                                <Button
                                    variant={theme === "dark" ? "pink" : "blue"}
                                    onClick={onClose}
                                >
                                    Go
                                </Button>
                            </div>
                        ) : (
                            <>
                                <p className={styles.selectText}>Select Your Spin Pack:</p>
                                <p className={styles.selectText2}>
                                    Unlock rewards, multiply your chances, win big.
                                </p>
                                {purchaseError && (
                                    <div className={styles.errorMessage}>
                                        <span>❌</span>
                                        <span>{purchaseError}</span>
                                    </div>
                                )}
                                <div className={styles.networkSelectWrapper}>
                                    <NetworkSelect
                                        selectedNetwork={selectedNetwork}
                                        onChange={setSelectedNetwork}
                                    />
                                </div>
                                <div className={styles.tokenToggle}>
                                    <TokenSelect
                                        selectedToken={selectedToken}
                                        onChange={setSelectedToken}
                                    />
                                </div>
                                <div className={styles.packageCards}>
                                    <div className={styles.packageCard}>
                                        <img
                                            src={oneSpin}
                                            alt="starter pack"
                                            className={styles.packageImage}
                                        />
                                        <p className={styles.packageText}>
                                            {TICKET_OPTIONS.small.label} — {TICKET_OPTIONS.small.ticketCount} Spins
                                        </p>
                                        <Button
                                            disabled={loading}
                                            onClick={() => handlePurchase("small")}
                                            variant={theme === "dark" ? "pink" : "blue"}
                                        >
                                            {loading && <CircleLoader isLoading={loading}/>}
                                            ${TICKET_OPTIONS.small.price} (${TICKET_OPTIONS.small.pricePerSpin} each)
                                        </Button>
                                    </div>
                                    <div className={styles.packageCard}>
                                        <img
                                            src={threeSpins}
                                            alt="value pack"
                                            className={styles.packageImage}
                                        />
                                        <p className={styles.packageText}>
                                            {TICKET_OPTIONS.medium.label} — {TICKET_OPTIONS.medium.ticketCount} Spins
                                        </p>
                                        <Button
                                            disabled={loading}
                                            onClick={() => handlePurchase("medium")}
                                            variant={theme === "dark" ? "pink" : "blue"}
                                        >
                                            {loading && <CircleLoader isLoading={loading}/>}
                                            ${TICKET_OPTIONS.medium.price} (${TICKET_OPTIONS.medium.pricePerSpin.toFixed(2)} each)
                                        </Button>
                                    </div>
                                    <div className={styles.packageCard}>
                                        <img
                                            src={fiveSpins}
                                            alt="best deal"
                                            className={styles.packageImage}
                                        />
                                        <p className={styles.packageText}>
                      <span className={styles.bestDeal}>
                        {TICKET_OPTIONS.big.label} — {TICKET_OPTIONS.big.ticketCount} Spins
                      </span>
                                        </p>
                                        <Button
                                            disabled={loading}
                                            onClick={() => handlePurchase("big")}
                                            variant={theme === "dark" ? "pink" : "blue"}
                                        >
                                            {loading && <CircleLoader isLoading={loading}/>}
                                            ${TICKET_OPTIONS.big.price} (${TICKET_OPTIONS.big.pricePerSpin.toFixed(2)} each)
                                        </Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {spinError && (
                <div className={styles.modal}>
                    <div
                        className={`${styles.modalContent} ${
                            theme === "dark" ? styles.darkTheme : ""
                        }`}
                    >
                        <button
                            className={styles.modalClose}
                            onClick={() => setSpinError(null)}
                        >
                            ×
                        </button>
                        <h1 className={styles.modalTitle}>Oops! 😕</h1>
                        <div className={styles.errorMessage}>
                            <span>⚠️</span>
                            <span>{spinError}</span>
                        </div>
                        <Button variant="pink" onClick={() => setSpinError(null)}>
                            Got it
                        </Button>
                    </div>
                </div>
            )}
            {isTwitterModalOpen && _id && (
                <TwitterRepostModal onClose={closeTwitterModal}/>
            )}
        </div>
    );
};

export default CaseOpener;
